import React from 'react';
import { motion } from 'framer-motion';
import { Button } from './ui/button';
import { Sparkles, ArrowRight, Star, MapPin } from 'lucide-react';

const BRAND = {
  pink: '#FF3B6B',
  orange: '#FB923C',
  yellow: '#FBBF24',
  teal: '#06D6A0',
  indigo: '#6366F1',
  ink: '#1E1E24',
  cream: '#FFFDF8',
};

/**
 * HeroCelebrationVariant — a fresh, vibrant hero for the Creative Birthdays
 * landing page. Designed for kid-party energy with a faded birthday photo
 * behind a colourful gradient + texture, large centred copy, two CTAs, and a
 * small testimonial badge underneath.
 *
 * Every visual is editable via the CMS Landing Page Editor:
 *   - `bg_image_url`      (uploaded image — opacity dialled down so text is legible)
 *   - `bg_overlay_opacity` (0.0–1.0, default 0.25 — how visible the photo is)
 *   - `gradient_start/mid/end` (re-used from the flash hero)
 *   - `badge_text`         (eyebrow chip above the headline)
 *   - `headline`           (single line — no animated word array required)
 *   - `headline_emoji`     (small rotating accent)
 *   - `subheadline`
 *   - `location_line`
 *   - `primary_cta_text`, `secondary_cta_text`
 *   - `testimonial_quote`, `testimonial_author`, `testimonial_rating` (1-5)
 *   - `align`              ('left' | 'center', default 'center')
 */
export const HeroCelebrationVariant = ({ cms = {}, onPrimary, onOpenAvailability }) => {
  const heroBg = `linear-gradient(135deg, ${cms.gradient_start || '#FFDEE9'} 0%, ${cms.gradient_mid || '#FFF4D6'} 45%, ${cms.gradient_end || '#B5FFFC'} 100%)`;
  const overlayOpacity = Math.min(1, Math.max(0, Number(cms.bg_overlay_opacity ?? 0.25)));
  const align = cms.align === 'left' ? 'left' : 'center';
  const headline = cms.headline || 'Create unforgettable birthdays';
  const headlineEmoji = cms.headline_emoji ?? '🎉';
  const badgeText = cms.badge_text || 'Creative Birthdays · Oakville';
  const subheadline = cms.subheadline || 'Hands-on craft stations, custom cakes, allergy-safe menus and gift bags the kids make themselves. Book your stress-free party today.';
  const locationLine = cms.location_line || 'Inside Edible Arrangements Oakville';
  const primaryCta = cms.primary_cta_text || 'Book a Party';
  const secondaryCta = cms.secondary_cta_text || 'Check Availability';
  const tQuote = cms.testimonial_quote || 'The most magical birthday we have ever thrown — our daughter is still talking about it!';
  const tAuthor = cms.testimonial_author || '— Priya M., real parent';
  const tRating = Math.min(5, Math.max(0, parseInt(cms.testimonial_rating, 10) || 5));

  return (
    <section
      className="relative overflow-hidden min-h-[92vh] flex items-center"
      style={{ background: heroBg }}
      data-testid="hero-celebration-variant"
    >
      {/* Faded birthday-photo overlay (editable image + opacity) */}
      {cms.bg_image_url && (
        <div
          className="absolute inset-0 pointer-events-none"
          style={{
            backgroundImage: `url(${cms.bg_image_url})`,
            backgroundSize: 'cover',
            backgroundPosition: 'center',
            opacity: overlayOpacity,
          }}
          aria-hidden
          data-testid="hero-cel-bg-image"
        />
      )}

      {/* Texture: confetti dot pattern */}
      <div
        className="absolute inset-0 opacity-50 pointer-events-none"
        style={{
          backgroundImage: `radial-gradient(circle at 1px 1px, ${BRAND.pink}1F 1px, transparent 0)`,
          backgroundSize: '28px 28px',
        }}
        aria-hidden
      />

      {/* Soft vignette to push edges back and center the copy */}
      <div
        className="absolute inset-0 pointer-events-none"
        style={{
          background: 'radial-gradient(ellipse at center, transparent 0%, rgba(255,255,255,0.35) 100%)',
        }}
        aria-hidden
      />

      {/* Floating accent emojis */}
      {[
        { emoji: '🎈', x: '6%', y: '10%', delay: 0 },
        { emoji: '🎂', x: '88%', y: '14%', delay: 0.4 },
        { emoji: '✨', x: '14%', y: '78%', delay: 0.8 },
        { emoji: '🎁', x: '82%', y: '74%', delay: 1.2 },
      ].map((f, i) => (
        <motion.span
          key={i}
          aria-hidden
          className="absolute select-none text-4xl sm:text-5xl pointer-events-none drop-shadow"
          style={{ left: f.x, top: f.y }}
          initial={{ y: 0, opacity: 0 }}
          animate={{ y: [0, -16, 0], opacity: 0.9 }}
          transition={{ duration: 5 + i * 0.4, delay: f.delay, repeat: Infinity, ease: 'easeInOut' }}
        >
          {f.emoji}
        </motion.span>
      ))}

      {/* Main content */}
      <div
        className={`relative z-10 container mx-auto px-4 py-16 sm:py-24 max-w-4xl ${align === 'center' ? 'text-center' : 'text-left'}`}
      >
        {/* Eyebrow badge */}
        <motion.div
          initial={{ opacity: 0, y: -8 }} animate={{ opacity: 1, y: 0 }} transition={{ delay: 0.1 }}
          className={`inline-flex items-center gap-2 px-4 py-1.5 rounded-full bg-white/90 backdrop-blur border border-white shadow-sm text-sm font-medium text-[#1E1E24] mb-6`}
          data-testid="hero-cel-badge"
        >
          <Sparkles className="w-4 h-4 text-[#FF3B6B]" />
          {badgeText}
        </motion.div>

        {/* Headline */}
        <motion.h1
          initial={{ opacity: 0, y: 12 }} animate={{ opacity: 1, y: 0 }} transition={{ delay: 0.2 }}
          className="text-4xl sm:text-5xl lg:text-6xl font-bold text-[#1E1E24] leading-[1.05] mb-5"
          style={{ fontFamily: 'Fredoka, sans-serif' }}
          data-testid="hero-cel-headline"
        >
          {headline}{' '}
          <motion.span
            className="inline-block"
            animate={{ rotate: [0, 12, -8, 0], scale: [1, 1.15, 1] }}
            transition={{ duration: 2, repeat: Infinity, ease: 'easeInOut' }}
          >{headlineEmoji}</motion.span>
        </motion.h1>

        {/* Subheadline */}
        <motion.p
          initial={{ opacity: 0, y: 8 }} animate={{ opacity: 1, y: 0 }} transition={{ delay: 0.3 }}
          className={`text-lg sm:text-xl text-[#1E1E24]/80 mb-3 max-w-2xl ${align === 'center' ? 'mx-auto' : ''}`}
          data-testid="hero-cel-subheadline"
        >
          {subheadline}
        </motion.p>

        {/* Location line */}
        {locationLine && (
          <motion.p
            initial={{ opacity: 0 }} animate={{ opacity: 1 }} transition={{ delay: 0.35 }}
            className="inline-flex items-center gap-2 text-sm font-medium text-[#1E1E24] bg-white/80 backdrop-blur border border-white rounded-full px-3.5 py-1.5 mb-8"
            data-testid="hero-cel-location"
          >
            <MapPin className="w-3.5 h-3.5 text-[#FF3B6B]" />
            {locationLine}
          </motion.p>
        )}

        {/* CTAs */}
        <motion.div
          initial={{ opacity: 0, y: 8 }} animate={{ opacity: 1, y: 0 }} transition={{ delay: 0.4 }}
          className={`flex flex-col sm:flex-row gap-3 ${align === 'center' ? 'justify-center' : ''} mb-10`}
        >
          <Button
            onClick={onPrimary}
            className="rounded-full px-8 py-6 text-base font-semibold bg-[#FF3B6B] hover:bg-[#E62E58] text-white shadow-lg hover:shadow-xl hover:-translate-y-0.5 transition-all"
            data-testid="hero-cel-cta-primary"
          >
            {primaryCta} <ArrowRight className="w-4 h-4 ml-2" />
          </Button>
          <Button
            onClick={onOpenAvailability}
            variant="outline"
            className="rounded-full px-8 py-6 text-base font-semibold bg-white/95 backdrop-blur border-2 border-[#1E1E24]/10 hover:bg-white hover:border-[#FF3B6B] text-[#1E1E24]"
            data-testid="hero-cel-cta-secondary"
          >
            {secondaryCta}
          </Button>
        </motion.div>

        {/* Testimonial badge */}
        <motion.div
          initial={{ opacity: 0, y: 8 }} animate={{ opacity: 1, y: 0 }} transition={{ delay: 0.5 }}
          className={`inline-flex flex-col gap-2 px-5 py-3 rounded-2xl bg-white/95 backdrop-blur border border-white shadow-md max-w-md ${align === 'center' ? 'mx-auto' : ''}`}
          data-testid="hero-cel-testimonial"
        >
          {tRating > 0 && (
            <div className={`flex items-center gap-0.5 ${align === 'center' ? 'justify-center' : ''}`}>
              {Array.from({ length: 5 }).map((_, i) => (
                <Star
                  key={i}
                  className={`w-3.5 h-3.5 ${i < tRating ? 'text-[#FBBF24] fill-[#FBBF24]' : 'text-[#E8E5DF]'}`}
                />
              ))}
            </div>
          )}
          <p className={`text-sm italic text-[#1E1E24] leading-snug ${align === 'center' ? 'text-center' : ''}`}>
            “{tQuote}”
          </p>
          <p className={`text-xs font-semibold text-[#6B6B7A] ${align === 'center' ? 'text-center' : ''}`}>
            {tAuthor}
          </p>
        </motion.div>
      </div>
    </section>
  );
};

export default HeroCelebrationVariant;
